/** Fortschritts-Matrix: Schüler als Zeilen, Fachbereiche als Spalten, je Fachbereich
 *  die Unterspalten Absolviert / Geplant / Soll plus ein Balken darunter.
 *
 *  Die Stunden gelten über alle Planungszeiträume hinweg, genau wie im Tab
 *  "Fortschritt" der Schülerakte. Die Soll-Berechnung ist bewusst dieselbe Formel
 *  (Pflichtstunden + Pool, Pool ggf. pro Schüler überschrieben), damit beide
 *  Ansichten nie auseinanderlaufen.
 *
 *  Angezeigt wird immer nur eine Fachbereichszuordnung, damit alle Zeilen dieselben
 *  Spalten haben. Klassen ohne Zuordnung und Schüler ohne Klasse bleiben außen vor.
 *
 *  Sie liegt fest auf der Seite #/auswertungen?bereich=praktikum. Bis zum
 *  Auswertungsbereich war sie ein Fenster, das die Planung geöffnet hat; der Knopf
 *  dort führt jetzt auf die Seite, deshalb gibt es nur noch diesen einen Einstieg.
 */
import { api } from "../api";
import { openModal } from "./modal";
import { Icons } from "../utils/icons";
import { layoutStickyTotals } from "../utils/sticky-totals";
import { fitOnResize } from "../utils/fit-height";
import { initDropdownCheck, initDropdownSelect, type DropdownCheckHandle } from "./dropdown-check";
import { exportFortschritt, type ProgressExportData, type ProgressExportCell } from "../utils/export-service";
import type {
  Student, SchoolClass, Department, Kreisverband,
  PlanningProgressResponse, AdminStudentAssignment,
} from "../types";
import { esc } from "../utils/html";

export interface ProgressMatrixContext {
  students: Student[];
  classes: SchoolClass[];
  departments: Department[];
  /** groupId → Fachbereich-IDs, aufgebaut in der Planung aus den Fachbereichsgruppen. */
  groupDeptIds: Map<number, Set<string>>;
  customGroupDeptIds: Map<number, Set<string>>;
  kreisverbandEnabled: boolean;
  kreisverbaende: Kreisverband[];
  /** Vorbelegung aus der Planung. Leer = alle Klassen. "none" = ohne Klasse. */
  filterClasses: (number | "none")[];
  filterKreisverband: number | "none" | undefined;
}

/** Eine Fachbereichszuordnung mit den Klassen, die sie verwenden. */
interface DeptGroup { key: string; label: string; classIds: number[]; deptIds: Set<string>; }

interface Cell { completed: number; planned: number; target: number; overridden: boolean; }

/** Das Element, in das gezeichnet wird. Setzt renderProgressMatrixInto(). */
let hostEl: HTMLElement | null = null;

function host(): HTMLElement {
  return hostEl!;
}

let ctx: ProgressMatrixContext;
let groups: DeptGroup[] = [];
let activeGroupKey = "";
let selectedClassIds: number[] = [];
let filterKv: number | "none" | undefined;
let search = "";
let sortKey = "name";
let sortAsc = true;
let mixedGroupsHint = false;
let excludedCount = 0;

/** Aufgeklappte Fachbereichs-Zellgruppen, Schlüssel "studentId|department". */
const expanded = new Set<string>();
/** Einsätze je Schüler, über alle Zeiträume. Wird beim Aufklappen nachgeladen. */
const assignmentCache = new Map<number, AdminStudentAssignment[]>();

let hoursMap = new Map<string, { completed: number; planned: number }>();
let overrideMap = new Map<string, number>();
let deptDefaults = new Map<string, { requiredHours: number; poolHours: number }>();

let classDropdown: DropdownCheckHandle | null = null;
let controlsAC: AbortController | null = null;

/** Aufgehellte Variante der Fachbereichsfarbe für den "geplant"-Anteil des Balkens. */
function lightenColor(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16) || 0;
  const g = parseInt(hex.slice(3, 5), 16) || 0;
  const b = parseInt(hex.slice(5, 7), 16) || 0;
  return `rgb(${Math.round(r * 0.5 + 255 * 0.5)},${Math.round(g * 0.5 + 255 * 0.5)},${Math.round(b * 0.5 + 255 * 0.5)})`;
}

function formatDate(s: string): string {
  const [y, m, d] = String(s).slice(0, 10).split("-");
  return `${d}.${m}.${y}`;
}

// ── Zuordnungen und Zellwerte ────────────────────────────────────────────────

/** Schlüssel der Fachbereichszuordnung einer Klasse. Leer = keine Zuordnung. */
function groupKeyOfClass(c: SchoolClass): string {
  if (c.customGroupId) return `c:${c.customGroupId}`;
  if (c.departmentGroupId) return `g:${c.departmentGroupId}`;
  return "";
}

function buildGroups(): DeptGroup[] {
  const byKey = new Map<string, DeptGroup>();
  for (const c of ctx.classes) {
    const key = groupKeyOfClass(c);
    if (!key) continue;
    let g = byKey.get(key);
    if (!g) {
      const deptIds = c.customGroupId
        ? ctx.customGroupDeptIds.get(+c.customGroupId)
        : ctx.groupDeptIds.get(+c.departmentGroupId!);
      // Ohne bekannte Fachbereichsliste ist die Zuordnung nicht darstellbar.
      if (!deptIds) continue;
      g = {
        key,
        label: (c.customGroupName || c.departmentGroupName || "Fachbereiche") as string,
        classIds: [],
        deptIds,
      };
      byKey.set(key, g);
    }
    g.classIds.push(+c.id);
  }
  return Array.from(byKey.values()).sort((a, b) => a.label.localeCompare(b.label, "de"));
}

function activeGroup(): DeptGroup | undefined {
  return groups.find(g => g.key === activeGroupKey);
}

function cellOf(studentId: number, deptId: string): Cell {
  const key = `${studentId}|${deptId}`;
  const h = hoursMap.get(key);
  const def = deptDefaults.get(deptId);
  const required = def?.requiredHours ?? 0;
  const pool = overrideMap.has(key) ? overrideMap.get(key)! : (def?.poolHours ?? 0);
  return {
    completed: h?.completed ?? 0,
    planned: h?.planned ?? 0,
    target: required + pool,
    overridden: overrideMap.has(key),
  };
}

/** Fachbereiche der aktiven Zuordnung, ohne die, die für niemanden eine Rolle spielen. */
function visibleDepartments(students: Student[]): Department[] {
  const g = activeGroup();
  if (!g) return [];
  return ctx.departments
    .filter(d => g.deptIds.has(d.id))
    .filter(d => students.some(s => {
      const c = cellOf(+s.id, d.id);
      return c.target > 0 || c.completed > 0 || c.planned > 0;
    }))
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
}

/** Schüler der aktiven Zuordnung nach Klassen- und Kreisverband-Filter.
 *  Die Namenssuche bleibt optional, weil sie die Spalten nicht verändern darf:
 *  sonst würden beim Tippen Fachbereichsspalten verschwinden. */
function filteredStudents(applySearch: boolean): Student[] {
  const g = activeGroup();
  if (!g) return [];
  const allowed = new Set(selectedClassIds.length > 0 ? selectedClassIds : g.classIds);
  const q = search.trim().toLowerCase();
  return ctx.students.filter(s => {
    if (!s.classId || !allowed.has(+s.classId)) return false;
    if (ctx.kreisverbandEnabled && filterKv !== undefined) {
      if (filterKv === "none") { if (s.drkKreisverbandId) return false; }
      else if (+(s.drkKreisverbandId ?? 0) !== filterKv) return false;
    }
    if (applySearch && q && !`${s.firstName} ${s.lastName}`.toLowerCase().includes(q)) return false;
    return true;
  });
}

function visibleStudents(): Student[] { return filteredStudents(true); }

function sumCells(cells: Cell[]): ProgressExportCell {
  return cells.reduce(
    (acc, c) => ({ completed: acc.completed + c.completed, planned: acc.planned + c.planned, target: acc.target + c.target }),
    { completed: 0, planned: 0, target: 0 }
  );
}

function sortValue(s: Student, depts: Department[]): number | string {
  if (sortKey === "name") return `${s.lastName || ""} ${s.firstName || ""}`.toLowerCase();
  const [scope, field] = sortKey.split("|");
  const cells = scope === "total" ? depts.map(d => cellOf(+s.id, d.id)) : [cellOf(+s.id, scope)];
  const sum = sumCells(cells);
  if (field === "abs") return sum.completed;
  if (field === "gepl") return sum.planned;
  if (field === "soll") return sum.target;
  // Erfüllungsgrad inklusive geplanter Stunden; ohne Soll ans Ende sortieren.
  return sum.target > 0 ? (sum.completed + sum.planned) / sum.target : Number.POSITIVE_INFINITY;
}

// ── Rendering ────────────────────────────────────────────────────────────────

function num(n: number): string {
  const v = Math.round(n);
  return v === 0 ? `<span class="pm-zero">0</span>` : String(v);
}

function barHtml(c: Cell, color: string): string {
  if (c.target <= 0) return `<div class="pm-bar"></div>`;
  const pctCompleted = Math.min(c.completed / c.target, 1) * 100;
  const pctTotal = Math.min((c.completed + c.planned) / c.target, 1) * 100;
  const light = lightenColor(color);
  return `<div class="pm-bar">
    ${pctTotal > 0 ? `<div class="pm-bar-fill" style="width:${pctTotal}%;background:${light}"></div>` : ""}
    ${pctCompleted > 0 ? `<div class="pm-bar-fill" style="width:${pctCompleted}%;background:${color}"></div>` : ""}
  </div>`;
}

/** Erklärung der Unterspalten, als Tooltip am Spaltenkopf. Die Kurzform steht
 *  zusätzlich als Legende über der Tabelle. */
const SUB_TITLES = {
  abs: "Absolviert: Stunden aus Einsätzen, die bereits beendet sind",
  gepl: "Geplant: Stunden aus Einsätzen, die noch laufen oder erst bevorstehen",
  soll: "Soll: Pflichtstunden plus Pool-Stunden, ggf. individuell für den Schüler",
};

function sortMark(key: string): string {
  if (sortKey !== key) return "";
  return sortAsc ? " ▲" : " ▼";
}

function sortClass(key: string): string {
  return sortKey === key ? " pm-sort-active" : "";
}

function detailRowHtml(studentId: number, dept: Department, colCount: number): string {
  const cached = assignmentCache.get(studentId);
  let inner: string;
  if (!cached) {
    inner = `<div class="pm-detail-empty">Einsätze werden geladen...</div>`;
  } else {
    const list = cached.filter(a => a.department === dept.id);
    inner = list.length === 0
      ? `<div class="pm-detail-empty">Keine Einsätze in diesem Fachbereich.</div>`
      : list.map(a => {
        const name = a.practiceName || (a.isFreeInput ? "Freier Einsatz" : "Ohne Praktikumsstelle");
        const status = a.isCurrent ? "läuft" : a.isUpcoming ? "geplant" : "absolviert";
        const hours = a.durationWeeks * a.hoursPerWeek;
        return `<div class="pm-detail-item">
            <span>${esc(name)}</span>
            <span class="pm-detail-meta">${formatDate(a.startDate)} bis ${formatDate(a.endDate)} · ${hours} Std. · ${status} · ${esc(a.periodName)}</span>
          </div>`;
      }).join("");
  }
  return `<tr class="pm-detail"><td colspan="${colCount}">
    <div class="pm-detail-inner">
      <div class="pm-detail-title">${esc(dept.nameDe)}</div>
      ${inner}
    </div></td></tr>`;
}

function tableHtml(): string {
  const students = visibleStudents();
  const depts = visibleDepartments(filteredStudents(false));
  if (depts.length === 0) return `<div class="pm-empty">Für diese Auswahl sind keine Fachbereiche hinterlegt.</div>`;
  if (students.length === 0) return `<div class="pm-empty">Keine Schüler für diese Auswahl.</div>`;

  const colCount = 1 + depts.length * 3 + 3;
  const classNameOf = (s: Student) => ctx.classes.find(c => +c.id === +(s.classId ?? 0))?.name || "";

  const sorted = [...students].sort((a, b) => {
    const va = sortValue(a, depts), vb = sortValue(b, depts);
    const cmp = typeof va === "string" ? va.localeCompare(vb as string, "de") : (va as number) - (vb as number);
    return sortAsc ? cmp : -cmp;
  });

  const distinctClasses = new Set(sorted.map(s => classNameOf(s)));
  const groupByClass = distinctClasses.size > 1;
  if (groupByClass) {
    // Klassen bleiben zusammen, die gewählte Sortierung wirkt innerhalb der Klasse.
    sorted.sort((a, b) => classNameOf(a).localeCompare(classNameOf(b), "de"));
  }

  const head = `<thead>
    <tr>
      <th class="pm-name pm-sortable${sortClass("name")}" data-sort="name">Schüler${sortMark("name")}</th>
      ${depts.map(d => `<th class="pm-dept-head pm-sortable${sortClass(`${d.id}|pct`)}" colspan="3" data-sort="${esc(d.id)}|pct" title="Nach Erfüllungsgrad sortieren"><span class="pm-dept-color" style="background:${esc(d.color)}"></span>${esc(d.nameDe)}${sortMark(`${d.id}|pct`)}</th>`).join("")}
      <th class="pm-dept-head pm-total pm-sortable${sortClass("total|pct")}" colspan="3" data-sort="total|pct" title="Nach Erfüllungsgrad sortieren">Gesamt${sortMark("total|pct")}</th>
    </tr>
    <tr>
      <th class="pm-name"></th>
      ${[...depts.map(d => d.id), "total"].map(id => {
        // Der Gesamtblock bleibt rechts stehen; pm-total markiert seine Zellen fuer
        // den Messlauf in layoutStickyTotals.
        const t = id === "total" ? " pm-total" : "";
        return `
        <th class="pm-num pm-sortable${t}${sortClass(`${id}|abs`)}" data-sort="${esc(id)}|abs" title="${SUB_TITLES.abs}">Abs.${sortMark(`${id}|abs`)}</th>
        <th class="pm-num pm-sortable${t}${sortClass(`${id}|gepl`)}" data-sort="${esc(id)}|gepl" title="${SUB_TITLES.gepl}">Gepl.${sortMark(`${id}|gepl`)}</th>
        <th class="pm-num pm-sortable${t}${sortClass(`${id}|soll`)}" data-sort="${esc(id)}|soll" title="${SUB_TITLES.soll}">Soll${sortMark(`${id}|soll`)}</th>`;
      }).join("")}
    </tr>
  </thead>`;

  let lastClass: string | null = null;
  const body = sorted.map(s => {
    const cells = depts.map(d => cellOf(+s.id, d.id));
    const total = sumCells(cells);
    const cls = classNameOf(s);
    const sep = groupByClass && cls !== lastClass
      ? `<tr class="pm-class-sep"><td colspan="${colCount}"><span>${esc(cls || "Ohne Klasse")}</span></td></tr>`
      : "";
    lastClass = cls;

    const numCells = cells.map((c, i) => {
      const d = depts[i];
      const open = expanded.has(`${s.id}|${d.id}`) ? " pm-open" : "";
      const attrs = `class="pm-num pm-cell${open}" data-student="${s.id}" data-dept="${esc(d.id)}"`;
      const soll = c.overridden
        ? `<span class="pm-ind" title="Individuelle Pool-Verteilung">${Math.round(c.target)}</span>`
        : num(c.target);
      return `<td ${attrs}>${num(c.completed)}</td><td ${attrs}>${num(c.planned)}</td><td ${attrs}>${soll}</td>`;
    }).join("");

    const barCells = cells.map((c, i) => {
      const d = depts[i];
      const open = expanded.has(`${s.id}|${d.id}`) ? " pm-open" : "";
      return `<td class="pm-cell${open}" colspan="3" data-student="${s.id}" data-dept="${esc(d.id)}">${barHtml(c, d.color)}</td>`;
    }).join("");

    const totalCell: Cell = { ...total, overridden: false };
    const details = depts
      .filter(d => expanded.has(`${s.id}|${d.id}`))
      .map(d => detailRowHtml(+s.id, d, colCount))
      .join("");

    return `${sep}
      <tr class="pm-row-a">
        <td class="pm-name"><div class="pm-student-name">${esc(s.firstName)} ${esc(s.lastName)}</div><div class="pm-student-sub">${esc(cls)}</div></td>
        ${numCells}
        <td class="pm-num pm-total">${num(total.completed)}</td><td class="pm-num pm-total">${num(total.planned)}</td><td class="pm-num pm-total">${num(total.target)}</td>
      </tr>
      <tr class="pm-row-b">
        <td class="pm-name"></td>
        ${barCells}
        <td class="pm-total" colspan="3">${barHtml(totalCell, "#2563eb")}</td>
      </tr>
      ${details}`;
  }).join("");

  const colSums = depts.map(d => sumCells(sorted.map(s => cellOf(+s.id, d.id))));
  const grand = sumCells(colSums.map(c => ({ ...c, overridden: false })));
  const foot = `<tfoot><tr>
    <td class="pm-name">Summe (${sorted.length} Schüler)</td>
    ${colSums.map(c => `<td class="pm-num">${Math.round(c.completed)}</td><td class="pm-num">${Math.round(c.planned)}</td><td class="pm-num">${Math.round(c.target)}</td>`).join("")}
    <td class="pm-num pm-total">${Math.round(grand.completed)}</td><td class="pm-num pm-total">${Math.round(grand.planned)}</td><td class="pm-num pm-total">${Math.round(grand.target)}</td>
  </tr></tfoot>`;

  return `<table class="pm-table">${head}<tbody>${body}</tbody>${foot}</table>`;
}

function renderTable() {
  const wrap = host().querySelector("#pm-table") as HTMLElement | null;
  if (!wrap) return;
  const top = wrap.scrollTop, left = wrap.scrollLeft;
  wrap.innerHTML = tableHtml();
  // Drei Unterspalten: Abs., Gepl., Soll.
  layoutStickyTotals(wrap, "pm-total", 3);
  wrap.scrollTop = top; wrap.scrollLeft = left;
}

/**
 * Was die Matrix bedeutet, als Fenster statt als Fliesstext ueber der Tabelle. Nach
 * dem ersten Lesen braucht man ihn nicht mehr, er hat die Tabelle aber dauerhaft nach
 * unten gedrueckt.
 */
function openInfo(): void {
  openModal("Wie diese Auswertung zu lesen ist", `
    <p class="aw-info-lead">Je Fachbereich stehen drei Zahlen nebeneinander, rechts dieselben drei über alle Fachbereiche.</p>
    <table class="aw-info-table">
      <tr><td><strong>Abs.</strong></td><td>${esc(SUB_TITLES.abs)}</td></tr>
      <tr><td><strong>Gepl.</strong></td><td>${esc(SUB_TITLES.gepl)}</td></tr>
      <tr><td><strong>Soll</strong></td><td>${esc(SUB_TITLES.soll)}</td></tr>
    </table>

    <h3 class="aw-info-head">Mehr sehen</h3>
    <ul class="aw-info-list">
      <li><strong>Ein Klick auf eine Zelle</strong> zeigt die einzelnen Einsätze des Schülers in diesem Fachbereich.</li>
      <li>Ein Klick auf eine <strong>Spaltenüberschrift</strong> sortiert danach, ein zweiter dreht die Richtung um.</li>
      <li>Der Balken unter den Zahlen zeigt den Erfüllungsgrad: kräftig das Absolvierte, blass das Geplante.</li>
    </ul>
    ${mixedGroupsHint || excludedCount > 0 ? `<h3 class="aw-info-head">Zu dieser Auswahl</h3><ul class="aw-info-list">
      ${mixedGroupsHint ? "<li>Der Planungsfilter enthielt auch Klassen einer anderen Fachbereichszuordnung. Zum Anzeigen oben umschalten.</li>" : ""}
      ${excludedCount > 0 ? `<li>${excludedCount} Schüler ohne Fachbereichszuordnung werden nicht angezeigt.</li>` : ""}
    </ul>` : ""}`, { wide: true });
}

/** Klassenliste an die aktive Zuordnung anpassen und alle Klassen vorwählen. */
function fillClassDropdown(preselect?: number[]) {
  const g = activeGroup();
  const drop = host().querySelector("#pm-class") as HTMLElement | null;
  if (!g || !drop) return;
  const panel = drop.querySelector(".dropdown-check-panel") as HTMLElement;
  const own = ctx.classes.filter(c => g.classIds.includes(+c.id));
  panel.innerHTML = own
    .map(c => `<label class="dropdown-check-item"><input type="checkbox" value="${c.id}" /> ${esc(c.name)}</label>`)
    .join("");
  selectedClassIds = (preselect || g.classIds).filter(id => g.classIds.includes(id));
  classDropdown?.setValues(selectedClassIds.map(String));
}

// ── Öffnen ───────────────────────────────────────────────────────────────────

/**
 * Zeichnet die Matrix in das übergebene Element. .pm-inline gibt ihr die Höhe, die
 * früher das feste Fensterformat vorgegeben hat.
 */
export async function renderProgressMatrixInto(el: HTMLElement, context: ProgressMatrixContext): Promise<void> {
  hostEl = el;
  el.classList.add("pm-inline");
  el.innerHTML = `<div class="pm-empty">Wird geladen...</div>`;
  await start(context);
}

async function start(context: ProgressMatrixContext): Promise<void> {
  ctx = context;
  // Zustand bewusst zuruecksetzen, damit ein erneutes Oeffnen nicht die alte
  // Sortierung oder aufgeklappte Zeilen erbt.
  expanded.clear();
  assignmentCache.clear();
  search = ""; sortKey = "name"; sortAsc = true;
  mixedGroupsHint = false;
  filterKv = ctx.filterKreisverband;
  groups = buildGroups();

  excludedCount = ctx.students.filter(s => {
    const c = ctx.classes.find(x => +x.id === +(s.classId ?? 0));
    return !c || !groupKeyOfClass(c) || !groups.some(g => g.key === groupKeyOfClass(c));
  }).length;

  if (groups.length === 0) {
    host().innerHTML = `<div class="pm-empty">Keine Klasse hat eine Fachbereichszuordnung. Die Zuordnung wird in den Klassen hinterlegt.</div>`;
    return;
  }

  let data: PlanningProgressResponse;
  try {
    data = await api.progress.list();
  } catch (err: any) {
    host().innerHTML = `<div class="pm-empty">Fortschritt konnte nicht geladen werden: ${esc(err.message)}</div>`;
    return;
  }

  hoursMap = new Map(data.hours.map(h => [`${h.studentId}|${h.department}`, { completed: h.completedHours, planned: h.plannedHours }]));
  overrideMap = new Map(data.overrides.map(o => [`${o.studentId}|${o.department}`, o.poolHours]));
  deptDefaults = new Map(data.departments.map(d => [d.department, { requiredHours: d.requiredHours, poolHours: d.poolHours }]));

  // Vorbelegung aus der Planung: die Zuordnung mit den meisten gefilterten Schuelern.
  const preClassIds = ctx.filterClasses.filter((v): v is number => v !== "none");
  const countPerGroup = new Map<string, number>();
  for (const s of ctx.students) {
    const c = ctx.classes.find(x => +x.id === +(s.classId ?? 0));
    const key = c ? groupKeyOfClass(c) : "";
    if (!key || !groups.some(g => g.key === key)) continue;
    if (preClassIds.length > 0 && !preClassIds.includes(+c!.id)) continue;
    countPerGroup.set(key, (countPerGroup.get(key) || 0) + 1);
  }
  activeGroupKey = groups
    .map(g => g.key)
    .sort((a, b) => (countPerGroup.get(b) || 0) - (countPerGroup.get(a) || 0))[0];
  // Nur melden, wenn der Planungsfilter selbst über mehrere Zuordnungen ging.
  // Ohne gesetzten Klassenfilter wäre der Hinweis irreführend.
  mixedGroupsHint = preClassIds.length > 0 && Array.from(countPerGroup.values()).filter(n => n > 0).length > 1;

  renderShell();
}

function renderShell() {
  const showGroups = groups.length > 1;
  const kvItems = ctx.kreisverbandEnabled
    ? `<div class="dropdown-check" id="pm-kv">
         <button type="button" class="dropdown-check-btn">Alle Kreisverbände</button>
         <div class="dropdown-check-panel dropdown-check-panel-left">
           <div class="dropdown-check-item" data-value="">Alle Kreisverbände</div>
           ${ctx.kreisverbaende.map(k => `<div class="dropdown-check-item" data-value="${k.id}">${esc(k.name)}</div>`).join("")}
           <div class="dropdown-divider"></div>
           <div class="dropdown-check-item" data-value="none">Ohne Kreisverband</div>
         </div>
       </div>`
    : "";

  host().innerHTML = `
    <div class="pm-wrap">
      <div class="pm-controls">
        ${showGroups ? `<div class="dropdown-check" id="pm-group">
          <button type="button" class="dropdown-check-btn">Fachbereichszuordnung</button>
          <div class="dropdown-check-panel dropdown-check-panel-left">
            ${groups.map(g => `<div class="dropdown-check-item" data-value="${esc(g.key)}">${esc(g.label)}</div>`).join("")}
          </div>
        </div>` : ""}
        <div class="dropdown-check" id="pm-class">
          <button type="button" class="dropdown-check-btn">Alle Klassen</button>
          <div class="dropdown-check-panel dropdown-check-panel-left"></div>
        </div>
        ${kvItems}
        <input type="search" class="pm-search" id="pm-search" placeholder="Schüler suchen" />
        <button type="button" class="btn btn-secondary btn-square" id="pm-info"
                title="Wie diese Auswertung zu lesen ist" aria-label="Wie diese Auswertung zu lesen ist">${Icons.informationCircle}</button>
        <div class="pm-spacer"></div>
        <div class="dropdown-check" id="pm-download">
          <button type="button" class="btn btn-secondary dropdown-check-btn">Download</button>
          <div class="dropdown-check-panel">
            <div class="dropdown-check-item" data-fmt="xlsx">Als Excel</div>
            <div class="dropdown-check-item" data-fmt="pdf">Als PDF</div>
          </div>
        </div>
      </div>
      <div class="pm-table-wrap" id="pm-table"></div>
    </div>`;

  const body = host();
  // Der naechste Aufbau loest den Listener der vorigen Ansicht ab; ein Schliessen,
  // an dem sich aufraeumen liesse, gibt es auf einer Seite nicht.
  controlsAC?.abort();
  controlsAC = new AbortController();

  body.querySelector("#pm-info")!.addEventListener("click", openInfo, { signal: controlsAC.signal });
  fitOnResize(body.querySelector<HTMLElement>("#pm-table")!, controlsAC.signal);

  // Die Dropdowns schliessen sich nicht von selbst, das erledigt sonst die Seite.
  document.addEventListener("click", (e) => {
    body.querySelectorAll(".dropdown-check.open").forEach(d => {
      if (!d.contains(e.target as Node)) d.classList.remove("open");
    });
  }, { signal: controlsAC.signal });

  if (showGroups) {
    const gDrop = body.querySelector("#pm-group") as HTMLElement;
    const handle = initDropdownSelect(gDrop, (v) => {
      activeGroupKey = v;
      mixedGroupsHint = false;
      expanded.clear();
      fillClassDropdown();
      renderTable();
    });
    handle.setValue(activeGroupKey);
  }

  const cDrop = body.querySelector("#pm-class") as HTMLElement;
  classDropdown = initDropdownCheck(cDrop, (vals) => {
    selectedClassIds = vals.map(Number);
    renderTable();
  }, { allSelectedShowsDefault: true });
  const preselect = ctx.filterClasses.filter((v): v is number => v !== "none");
  fillClassDropdown(preselect.length > 0 ? preselect : undefined);

  if (ctx.kreisverbandEnabled) {
    const kDrop = body.querySelector("#pm-kv") as HTMLElement;
    const kHandle = initDropdownSelect(kDrop, (v) => {
      filterKv = v === "" ? undefined : v === "none" ? "none" : +v;
      renderTable();
    });
    kHandle.setValue(filterKv === undefined ? "" : String(filterKv));
  }

  const searchEl = body.querySelector("#pm-search") as HTMLInputElement;
  searchEl.addEventListener("input", () => { search = searchEl.value; renderTable(); });

  const dDrop = body.querySelector("#pm-download") as HTMLElement;
  dDrop.querySelector(".dropdown-check-btn")!.addEventListener("click", () => dDrop.classList.toggle("open"));
  dDrop.querySelectorAll("[data-fmt]").forEach(item => {
    item.addEventListener("click", async () => {
      dDrop.classList.remove("open");
      try {
        await exportFortschritt((item as HTMLElement).dataset.fmt as "xlsx" | "pdf", buildExportData());
      } catch (err: any) { alert(err.message); }
    });
  });

  const tableWrap = body.querySelector("#pm-table") as HTMLElement;
  tableWrap.addEventListener("click", onTableClick);
  tableWrap.addEventListener("mouseover", onTableHover);
  tableWrap.addEventListener("mouseout", () => clearHighlight(tableWrap));

  renderTable();
}

// ── Interaktion ──────────────────────────────────────────────────────────────

function clearHighlight(root: HTMLElement) {
  root.querySelectorAll(".pm-hl").forEach(el => el.classList.remove("pm-hl"));
}

/** Die drei Zahlenzellen und die Balkenzelle eines Fachbereichs gemeinsam hervorheben,
 *  damit erkennbar ist, dass die ganze Gruppe ein Klickziel ist. */
function onTableHover(e: Event) {
  const root = e.currentTarget as HTMLElement;
  const td = (e.target as HTMLElement).closest("td.pm-cell") as HTMLElement | null;
  clearHighlight(root);
  if (!td) return;
  const { student, dept } = td.dataset;
  root.querySelectorAll(`td.pm-cell[data-student="${student}"][data-dept="${CSS.escape(dept || "")}"]`)
    .forEach(el => el.classList.add("pm-hl"));
}

async function onTableClick(e: Event) {
  const target = e.target as HTMLElement;

  const th = target.closest("th.pm-sortable") as HTMLElement | null;
  if (th) {
    const key = th.dataset.sort!;
    // Erneuter Klick dreht die Richtung. Zahlenspalten starten aufsteigend, damit
    // der groesste Rueckstand zuerst steht.
    if (sortKey === key) sortAsc = !sortAsc;
    else { sortKey = key; sortAsc = true; }
    renderTable();
    return;
  }

  const td = target.closest("td.pm-cell") as HTMLElement | null;
  if (!td) return;
  const studentId = +td.dataset.student!;
  const key = `${studentId}|${td.dataset.dept}`;
  if (expanded.has(key)) {
    expanded.delete(key);
    renderTable();
    return;
  }
  expanded.add(key);
  renderTable();
  if (!assignmentCache.has(studentId)) {
    try {
      assignmentCache.set(studentId, await api.students.planung(studentId));
    } catch {
      assignmentCache.set(studentId, []);
    }
    renderTable();
  }
}

// ── Export ───────────────────────────────────────────────────────────────────

function buildExportData(): ProgressExportData {
  const students = visibleStudents();
  const depts = visibleDepartments(filteredStudents(false));
  const classNameOf = (s: Student) => ctx.classes.find(c => +c.id === +(s.classId ?? 0))?.name || "";
  const sorted = [...students].sort((a, b) => {
    const c = classNameOf(a).localeCompare(classNameOf(b), "de");
    return c !== 0 ? c : `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`, "de");
  });

  const strip = (c: Cell): ProgressExportCell => ({
    completed: Math.round(c.completed), planned: Math.round(c.planned), target: Math.round(c.target),
  });

  const rows = sorted.map(s => {
    const cells = depts.map(d => cellOf(+s.id, d.id));
    return {
      studentName: `${s.firstName} ${s.lastName}`,
      className: classNameOf(s),
      cells: cells.map(strip),
      total: sumCells(cells),
    };
  });
  const sums = depts.map(d => sumCells(sorted.map(s => cellOf(+s.id, d.id))));
  const grandTotal = sums.reduce(
    (acc, c) => ({ completed: acc.completed + c.completed, planned: acc.planned + c.planned, target: acc.target + c.target }),
    { completed: 0, planned: 0, target: 0 }
  );

  return {
    groupLabel: groups.length > 1 ? (activeGroup()?.label || "") : "",
    columns: depts.map(d => ({ id: d.id, nameDe: d.nameDe })),
    rows,
    sums,
    grandTotal,
  };
}
